import { FC, useRef, forwardRef, useImperativeHandle, useMemo, useState, useContext, useEffect } from 'react';
import { TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import TaskDetailApproveInfo from '@/common/components/business/ExpandStore/TaskDetailApproveInfo';
import TaskBasic from './components/TaskBasic/index';
import Meet from './components/Meet';
import PointMatching from './components/PointMatching';
import styles from './index.module.less';
import BasicInfo from './components/BasicInfo.tsx';
import V2Tabs from '@/common/components/Data/V2Tabs';
import UserInfoContext from '@/layout/context';
import { globalAlias, globalExpansionTaskAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import { isArray, isNotEmptyAny } from '@lhb/func';
import FranchiseeInfo from './components/FranchiseeInfo';
import { useMethods } from '@lhb/hook';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import V2Flex from '@/common/components/Feedback/V2Flex';
import TaskTerminateInfo from '@/common/components/business/ExpandStore/TaskTerminateInfo';

enum TabKeys {
  /** 机会点 */
  changePoint = 'changePoint',
  /** 沟通记录 */
  communication = 'communication',
  /** 任务信息 */
  taskInfo = 'taskInfo',
  franchisee = 'franchisee',
}

/**
 * 拓店任务：加盟（益禾堂、非益禾堂）-详情，不含顶部操作按钮
 * @Description
 * 目前在拓店任务抽屉、拓店异动审批详情、机会点详情的关联任务信息栏使用
 */
const TaskDetail: FC<{
  /** 样式 */
  style?: any;
  // mainHeight, // 从V2Container透传过来的，必须挂到scroll上
  /** 拓店任务详情信息 */
  detail: any;
  /** 拓店任务详情抽屉是否打开 */
  open?: boolean;
  /** 是否审批详情内 */
  isApproval?: boolean;
  /** 审批详情 */
  aprDetail?: any;
  /** 主动刷新页面 */
  refresh?: Function;
  /** 是否需要主动隐藏操作按钮 */
  hideOperate?: boolean;
  /** 是否隐藏加盟商唯一标识字段 */
  hideFranchiseeUniqueId?: boolean;
  ref?: any;
}> = forwardRef(({
  style = {},
  // mainHeight, // 从V2Container透传过来的，必须挂到scroll上
  detail, // 拓店任务详情信息
  open = false, // 拓店任务详情抽屉是否打开
  isApproval = false, // 是否审批详情内
  aprDetail = {}, // 审批详情
  refresh, // 主动刷新页面
  hideOperate = false, // 是否需要主动隐藏操作按钮
  hideFranchiseeUniqueId = false,
}, ref) => {

  // 外部调用异动审批通过操作
  useImperativeHandle(ref, () => ({
    approveTaskChange: (cb: Function) => {
      (approveInfoRef?.current as any).approveTaskChange(cb);
    },
  }));

  const [chancePointInfo, setChancePointInfo] = useState<any>(null);
  const [franDetail, setFranDetail] = useState<any>({});
  const [active, setActive] = useState<TabKeys>(TabKeys.changePoint);
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);

  const approveInfoRef = useRef<any>(null);
  const pointMatchingRef = useRef<any>(null);
  const meetRef = useRef<any>(null);
  /** 展示外部的新增机会点按钮文案，为空则不展示按钮 */
  const [addPointButtonText, setAddPointButtonText] = useState('');
  /** 展示外部的新增沟通记录按钮文案，为空则不展示按钮 */
  const [addMeetButtonText, setAddMeetButtonText] = useState('');

  // 是否只能查看不能新增数据（审批详情或者拓店任务状态非进行中时）
  // 手动控制hideOperate
  const isView = isApproval || detail.status !== TaskStatus.Processing || hideOperate;

  const tabItems = useMemo(() => {
    const tabChance = {
      key: TabKeys.changePoint,
      label: `${globalAlias(globalAliasItemVOs)} ${chancePointInfo?.objectList?.length || 0}`,
    };
    const tabCommu = {
      key: TabKeys.communication,
      label: `沟通记录`,
    };
    const tabTask = {
      key: TabKeys.taskInfo,
      label: `${expansionTaskAlias || '拓店任务'}信息`,
    };
    const tabFran = {
      key: TabKeys.franchisee,
      label: `${franchiseeAlias}信息`,
    };
    // 待指派时优先展示任务信息，方便操作指派经理
    return detail?.status === TaskStatus.WaitAssign ? [
      tabTask,
      tabChance,
      tabCommu,
      tabFran
    ] : [
      tabChance,
      tabCommu,
      tabTask,
      tabFran
    ];
  }, [chancePointInfo, detail?.status, expansionTaskAlias]);

  // 是否有编辑任务的权限
  const hasTaskEditPerm = useMemo(() => {
    return (isArray(detail?.permissions) && !!detail.permissions.find((perm: any) => perm.event === 'standardTask:edit'));
  }, [detail]);

  useEffect(() => {
    const franId = detail?.franchiseeDetail?.id;
    franId && methods.getFranDetail(franId);
  }, [detail?.franchiseeDetail?.id]);

  useEffect(() => {
    // 待指派时优先展示任务信息，方便操作指派经理
    if (detail?.status === TaskStatus.WaitAssign) {
      setActive(TabKeys.taskInfo);
    }
  }, [detail?.status]);

  const methods = useMethods({

    /**
     * @description 获取加盟商详情
     */
    getFranDetail: (id: number) => {
      getFranchiseeDetail({ id }).then((res) => {
        res && setFranDetail(res);
      });
    },

    /**
     * @description 新增机会点
     */
    addChancePoint: () => {
      pointMatchingRef?.current?.addChancePoint();
    },
    /**
     * @description 新增沟通记录
     */
    addCommunicationRecord: () => {
      meetRef?.current?.addCommunicationRecord();
    },

  });
  setAddMeetButtonText;

  return (
    isNotEmptyAny(detail) ? <div
      className={styles.taskDetail}
      style={{
        // height: mainHeight || 'auto',
        // overflowY: 'scroll',
        // overflowX: 'hidden',
        ...style
      }}
    >

      <TaskTerminateInfo detail={detail} />

      {/* 异动审批信息 */}
      <TaskDetailApproveInfo
        ref={approveInfoRef}
        detail={detail}
        aprDetail={aprDetail}
        isApproval={isApproval}
      />

      <BasicInfo
        detail={detail}
        aprDetail={aprDetail}
        franchiseeAlias={franchiseeAlias}
        expansionTaskAlias={expansionTaskAlias}
        hideFranchiseeUniqueId={hideFranchiseeUniqueId}
      />

      <V2Tabs
        items={tabItems}
        tabBarExtraContent={{
          right: <V2Flex gap={10}>
            {active === TabKeys.changePoint && !isView && addPointButtonText && <a onClick={methods.addChancePoint}>{addPointButtonText}</a>}
            {active === TabKeys.communication && !isView && addMeetButtonText && <a onClick={methods.addCommunicationRecord}>{addMeetButtonText}</a>}
          </V2Flex>
        }}
        onChange={(value:any) => setActive(value)}
      />

      {/* 任务信息 */}
      {active === TabKeys.taskInfo ? <TaskBasic
        detail={detail}
        open={open}
        canEditType={!isView && hasTaskEditPerm}
        refresh={refresh}
        hideFranchiseeUniqueId={hideFranchiseeUniqueId}/> : <></>}

      {/* 匹配点位-机会点 */}
      {active === TabKeys.changePoint ? <PointMatching
        ref={pointMatchingRef}
        id={detail.id}
        taskDetail={detail}
        refresh={refresh}
        // chancePoints={detail.chancePoints}
        chancePointInfo={chancePointInfo}
        setChancePointInfo={setChancePointInfo}
        hideOperate={hideOperate}
        isView={isView}
        showAddBtn={false}
        setAddPointButtonText={setAddPointButtonText}
      /> : <></>}

      {/* 沟通记录 */}
      {active === TabKeys.communication ? <Meet
        ref={meetRef}
        id={detail.id}
        // open={open}
        // refresh={refresh}
        // meetList={detail.communicationRecords}
        isView={isView}
        showAddBtn={false}
        setAddMeetButtonText={setAddMeetButtonText}
      /> : <></>}


      {active === TabKeys.franchisee ? <FranchiseeInfo franDetail={franDetail}/> : <></>}
    </div> : <></>
  );
});

export default TaskDetail;
